<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import DeleteStageDialog from './DeleteStageDialog.vue';
/* global axios */

const props = defineProps({
  stage: { type: Object, default: null },
  pipelineId: { type: Number, required: true },
  accountId: { type: [String, Number], required: true },
  // Pipeline padre · si se pasa, se muestra info de sincronización con sucursales
  pipeline: { type: Object, default: null },
  // Cantidad de leads en esta columna (para el dialog de eliminación)
  dealsCount: { type: Number, default: 0 },
});

const emit = defineEmits(['close', 'updated', 'deleted']);

const showDeleteDialog = ref(false);

const store = useStore();

const PRESET_COLORS = [
  '#3b82f6', '#06b6d4', '#10b981', '#eab308', '#f97316',
  '#ef4444', '#ec4899', '#a855f7', '#14b8a6', '#84cc16',
];

const allBranches = computed(() => store.getters['branches/getBranches'] || []);
const syncInfo = computed(() => {
  if (!props.pipeline) return null;
  const scope = props.pipeline.scope_branch_ids || [];
  if (scope.length === 0) {
    return {
      type: 'global',
      text: `🌐 Esta columna pertenece al embudo "${props.pipeline.name}", visible en TODAS las ${allBranches.value.length} sucursales · los cambios se reflejan automáticamente en todas.`,
    };
  }
  const names = allBranches.value.filter(b => scope.includes(b.id)).map(b => b.name);
  return {
    type: 'specific',
    text: `🏢 Embudo activo en ${scope.length} sucursal${scope.length > 1 ? 'es' : ''}: ${names.join(', ')} · los cambios se aplican en todas estas.`,
  };
});

const name = ref('');
const color = ref('#3b82f6');
const stageType = ref('open');
const saving = ref(false);
const errorMsg = ref(null);

watch(
  () => props.stage,
  s => {
    if (s) {
      name.value = s.name;
      color.value = s.color;
      stageType.value = s.stage_type;
    }
  },
  { immediate: true }
);

async function save() {
  saving.value = true;
  errorMsg.value = null;
  try {
    const url = `/api/v1/accounts/${props.accountId}/pipelines/${props.pipelineId}/stages/${props.stage.id}`;
    const { data } = await axios.patch(url, {
      stage: { name: name.value, color: color.value, stage_type: stageType.value },
    });
    emit('updated', data);
  } catch (e) {
    errorMsg.value = e.response?.data?.message || e.message;
  } finally {
    saving.value = false;
  }
}

function openDeleteDialog() {
  showDeleteDialog.value = true;
}

function onStageDeleted(stageId) {
  showDeleteDialog.value = false;
  emit('deleted', stageId);
}

function onStageScopeUpdated(updated) {
  showDeleteDialog.value = false;
  // Si la sucursal activa ya no está en el scope, equivale a "borrada" para esta vista.
  const activeBranchId = store.getters['branches/getActiveBranchId'];
  const newScope = updated?.scope_branch_ids || [];
  if (activeBranchId && newScope.length > 0 && !newScope.includes(Number(activeBranchId))) {
    emit('deleted', props.stage.id);
  } else {
    emit('updated', updated);
  }
}
</script>

<template>
  <div v-if="stage" class="esd-overlay" @click.self="emit('close')">
    <div class="esd-modal" role="dialog" aria-modal="true">
      <div class="esd-head">
        <div class="esd-title-wrap">
          <div class="esd-title-icon">✏</div>
          <div>
            <div class="esd-title">Editar columna</div>
            <div class="esd-subtitle">{{ stage.name }}</div>
          </div>
        </div>
        <button class="esd-close" type="button" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <div class="esd-body">
        <div class="esd-field-label">Nombre</div>
        <input v-model="name" type="text" class="esd-input" />

        <div class="esd-section">
          <div class="esd-field-label">Color</div>
          <div class="esd-color-grid">
            <button v-for="c in PRESET_COLORS" :key="c" type="button" class="esd-color-swatch" :class="{ 'is-on': color === c }" :style="{ background: c }" @click="color = c" />
            <input v-model="color" type="color" class="esd-color-custom" />
          </div>
        </div>

        <div class="esd-section">
          <div class="esd-field-label">Tipo</div>
          <select v-model="stageType" class="esd-input esd-select">
            <option value="open">Abierto (en progreso)</option>
            <option value="won">Logrado (won)</option>
            <option value="lost">Perdido (lost)</option>
          </select>
        </div>

        <div v-if="syncInfo" class="esd-sync-info" :class="syncInfo.type">
          {{ syncInfo.text }}
        </div>

        <div v-if="errorMsg" class="esd-error">⚠ {{ errorMsg }}</div>
      </div>

      <div class="esd-foot">
        <button @click="openDeleteDialog" :disabled="saving" class="esd-btn-delete" type="button">🗑 Eliminar</button>
        <div style="flex: 1;"></div>
        <button @click="emit('close')" class="esd-btn esd-cancel" type="button">Cancelar</button>
        <button @click="save" :disabled="saving || !name" class="esd-btn esd-save" type="button">
          {{ saving ? '⏳ Guardando…' : '⚡ Guardar' }}
        </button>
      </div>
    </div>

    <DeleteStageDialog
      v-if="showDeleteDialog && stage && pipeline"
      :stage="stage"
      :pipeline="pipeline"
      :pipeline-id="pipelineId"
      :account-id="accountId"
      :deals-in-stage="dealsCount"
      @close="showDeleteDialog = false"
      @deleted="onStageDeleted"
      @updated="onStageScopeUpdated"
    />
  </div>
</template>

<style>
/* EditStageDialog · M1.B */
.esd-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(0,0,0,0.78); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 24px; font-family: -apple-system, system-ui, sans-serif; }
.esd-modal { width: 460px; max-width: 100%; background: rgba(10,10,10,0.98); border: 1.5px solid rgba(251,191,36,0.45); border-radius: 18px; overflow: hidden; box-shadow: 0 30px 90px rgba(0,0,0,0.85), 0 0 50px rgba(251,191,36,0.18); position: relative; color: #fff; }
.esd-modal::before { content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 18px; background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(251,191,36,0.10), transparent 60%); z-index: 1; }
.esd-modal > * { position: relative; z-index: 2; }

.esd-head { padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: space-between; align-items: center; background: radial-gradient(ellipse at 0% 0%, rgba(251,191,36,0.10), transparent 60%); }
.esd-title-wrap { display: flex; align-items: center; gap: 10px; }
.esd-title-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.10)); border: 1.5px solid rgba(251,191,36,0.40); display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
.esd-title { font-size: 16px; font-weight: 900; color: #fff; line-height: 1.15; }
.esd-subtitle { font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.esd-close { width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; line-height: 1; padding: 0; font-family: inherit; transition: all 0.15s; }
.esd-close:hover { background: rgba(239,68,68,0.20); color: #fff; border-color: rgba(239,68,68,0.40); transform: rotate(90deg); }

.esd-body { padding: 16px 20px; }
.esd-field-label { font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(255,255,255,0.55); font-weight: 800; margin-bottom: 6px; }
.esd-input { width: 100%; padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid rgba(251,191,36,0.30); border-radius: 7px; color: #fff; font-size: 13px; font-family: inherit; outline: none; }
.esd-input:focus { border-color: #fbbf24; box-shadow: 0 0 0 3px rgba(251,191,36,0.15); }
.esd-select option { background: #0a0a0a; color: #fff; }
.esd-section { margin-top: 14px; }

.esd-color-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 6px; }
.esd-color-swatch { width: 100%; aspect-ratio: 1; border-radius: 8px; border: 2px solid transparent; cursor: pointer; padding: 0; }
.esd-color-swatch.is-on { border-color: #fff; box-shadow: 0 0 12px rgba(251,191,36,0.45); transform: scale(1.05); }
.esd-color-custom { width: 100%; aspect-ratio: 1; border-radius: 8px; cursor: pointer; border: 1px solid rgba(255,255,255,0.18); background: transparent; padding: 0; }

.esd-sync-info { margin-top: 14px; padding: 10px 12px; border-radius: 8px; font-size: 11px; line-height: 1.5; }
.esd-sync-info.global { background: rgba(34,197,94,0.06); border: 1px dashed rgba(34,197,94,0.40); color: #4ade80; }
.esd-sync-info.specific { background: rgba(99,102,241,0.06); border: 1px dashed rgba(99,102,241,0.40); color: #a5b4fc; }

.esd-error { margin-top: 12px; padding: 10px 12px; background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.40); border-radius: 8px; font-size: 11px; color: #f87171; font-weight: 700; }

.esd-foot { padding: 12px 20px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; align-items: center; gap: 8px; background: rgba(0,0,0,0.4); }
.esd-btn-delete { background: rgba(239,68,68,0.10); color: #f87171; border: 1px solid rgba(239,68,68,0.40); padding: 8px 14px; border-radius: 7px; font-size: 11px; font-weight: 800; cursor: pointer; font-family: inherit; }
.esd-btn-delete:hover:not(:disabled) { background: rgba(239,68,68,0.20); }
.esd-btn-delete:disabled { opacity: 0.5; cursor: wait; }
.esd-btn { padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; }
.esd-btn:disabled { opacity: 0.5; cursor: wait; }
.esd-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.08); }
.esd-cancel:hover { background: rgba(255,255,255,0.08); }
.esd-save { background: linear-gradient(135deg, #fbbf24, #d97706); color: #000; box-shadow: 0 0 14px rgba(251,191,36,0.30); }
.esd-save:hover:not(:disabled) { box-shadow: 0 0 22px rgba(251,191,36,0.55); }
</style>
